import { CSRF_TOKEN_HEADER, IDEMPOTENCY_KEY_HEADER } from '@absurd-careers/shared';
import type { ApiErrorBody, ApiErrorCode, ApiFieldError, ApiSuccessBody } from '@absurd-careers/shared';

/** Empty when the API is served from the same origin, which is the default. */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

const BASE_URL = API_BASE_URL;

/** A failed request, carrying the field errors a form can attach to its inputs. */
export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fieldErrors: ApiFieldError[];
  readonly requestId?: string;

  constructor(
    status: number,
    body: ApiErrorBody['error'] | undefined,
    fallback = 'Something went wrong. Please try again.',
  ) {
    super(body?.message ?? fallback);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.code ?? 'INTERNAL_ERROR';
    this.fieldErrors = body?.fieldErrors ?? [];
    this.requestId = body?.requestId;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/**
 * The CSRF token is handed out with the session and echoed on every mutating
 * request. Holding it in memory keeps it out of storage a script could read.
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function parseError(response: Response): Promise<ApiErrorBody['error'] | undefined> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return body.error;
  } catch {
    return undefined;
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (method !== 'GET' && csrfToken) headers[CSRF_TOKEN_HEADER] = csrfToken;
  if (options.idempotencyKey) headers[IDEMPOTENCY_KEY_HEADER] = options.idempotencyKey;

  let body: BodyInit | undefined;
  if (options.formData) {
    // Let the browser set the multipart boundary.
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const response = await fetch(BASE_URL + path, {
    method,
    headers,
    body,
    credentials: 'include',
    signal: options.signal,
  });

  if (!response.ok) {
    throw new ApiRequestError(response.status, await parseError(response));
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json()) as ApiSuccessBody<T>;
  return payload.data;
}

/** Builds a query string, dropping empty values so filters stay readable in the URL. */
export function toQuery(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? '?' + query : '';
}
